import { useEffect, useRef, useState } from "react";
import RevolutCheckout from "@revolut/checkout";
import Loading from "../../loadingScreen/Loading";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import ErrorModal from "../../modals/errorModal/ErrorModal";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Skeleton } from "antd";
import { getTransactionStatus, selectTransactionData } from "../../../../slices/TransactionIdSlice";
import { getStorage } from "../../../../Utils/utils";

export default function RevolutPayButton({ handleInitiatePayment, handleInitiatePaymentAnalytics }) {
    const containerRef = useRef(null);
    const revolutPayRef = useRef(null);
    // Set when the retry-limit modal (global, BoxPayCheckout) is shown, so the
    // SDK's follow-up error/cancel event doesn't stack a second modal on top.
    const retryLimitReachedRef = useRef(false);
    const [formLoading, setFormLoading] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [paymentCancelModal, setShowPaymentCancelModal] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);
    const [buttonReady, setButtonReady] = useState(true);
    const dispatch = useDispatch()

    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
    const revolutPayConfig = sessionDetails.configs?.paymentMethods?.find(
        (el) => el.type?.toLowerCase() === 'wallet' && el.brand?.toLowerCase() === 'revolutpay'
    );
    const PUBLIC_TOKEN = revolutPayConfig?.additionalData?.publicKey
    const totalAmount = sessionDetails.paymentDetails?.amountInMinorUnits
    const CURRENCY = sessionDetails.paymentDetails?.money?.currencyCode;

    async function createOrder() {
        setFormLoading(true);
        retryLimitReachedRef.current = false;
        handleInitiatePaymentAnalytics("RevolutPay")
        const res = await handleInitiatePayment("wallet/revolutpay");
        if (res?.retryLimitReached) {
            retryLimitReachedRef.current = true;
            setFormLoading(false);
            return;
        }
        if (res?.data?.actions?.[1] && res.data.actions[1].url) {
            const redirectUrlTemp = res.data.actions[1].url;
            if (redirectUrlTemp) {
                setRedirectUrl(redirectUrlTemp);
            }
        }
        if (res?.data?.actions?.[0] && res.data.actions[0].token) {
            const token = res.data.actions[0].token;
            return { publicId: token }
        }
    }

    useEffect(() => {
        if (paymentCompleted) {
            if (redirectUrl) {
                window.location.replace(redirectUrl)
            } else {
                const token = sessionDetails.token
                const transactionId = getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
                dispatch(getTransactionStatus({ token, transactionId }));
            }
        }
    }, [paymentCompleted, redirectUrl]);

    useEffect(() => {
        let destroyed = false;
        async function init() {
            if (!containerRef.current) return;

            const payments = await RevolutCheckout.payments({
                locale: 'en',
                publicToken: PUBLIC_TOKEN,
            });

            if (destroyed) return;

            const revolutPay = payments.revolutPay;
            revolutPayRef.current = revolutPay;

            revolutPay.mount(containerRef.current, {
                currency: CURRENCY,
                totalAmount: totalAmount,
                createOrder: createOrder,
                hidePromotion: true,
                buttonStyle: {
                    height: "40px",
                    radius: "small",
                    variant: "dark",
                    width: "small",
                    action: "pay",
                    cashback: false
                },
            });
            revolutPay.on("ready", () => {
                setButtonReady(false);
            });

            revolutPay.on("payment", (event) => {
                if (event.type === "success") {
                    setPaymentCompleted(true);
                }
                if (event.type === "error") {
                    setFormLoading(false);
                    console.error("Payment error:", event)
                    handleInitiatePaymentAnalytics("RevolutPay/paymentInitiateError", JSON.stringify(event));
                    if (!retryLimitReachedRef.current) setShowErrorModal(true)
                }
                if (event.type === "cancel") {
                    setFormLoading(false);
                    if (!retryLimitReachedRef.current) setShowPaymentCancelModal(true)
                };
            });
        }

        init().catch(console.error);

        return () => {
            destroyed = true;
            revolutPayRef.current?.destroy?.();
            revolutPayRef.current = null;
        };
    }, []);

    return <div className="payment-container">
        <ErrorModal
            title={t("PAYMENT_CANCELLED")}
            desc={t("PAYMENT_RETRY_NOTICE")}
            showModal={paymentCancelModal}
            primaryOnClick={() => setShowPaymentCancelModal(false)}
            icon={<InfoCircleOutlined style={{ color: "#FF4D4F" }} />}
        />
        <ErrorModal
            title={t("PAYMENT_FAILED")}
            desc={t("PAYMENT_RETRY_NOTICE")}
            showModal={showErrorModal}
            primaryOnClick={() => setShowErrorModal(false)}
            icon={<InfoCircleOutlined style={{ color: "#FF4D4F" }} />}
        />

        <div ref={containerRef} style={{ width: "100%", display: !buttonReady ? "block" : "none" }} />
        {formLoading && (
            <Loading text={t("PLEASE_WAIT_PROCESSING_PAYMENT")} />
        )}
        {buttonReady && (
            <Skeleton.Button active style={{ width: "100%", height: 40 }} shape="default" />
        )}
    </div>;
}